import { Injectable } from '@angular/core';
import { BaseModalViewModel } from '../../../../../models/base/base-modal-view-model';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { LocationDomainModel } from '../../../../../domainModels/location-domain-model';
import { DisplayDomainModel } from '../../../../../domainModels/display-domain-model';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { map, shareReplay } from 'rxjs/operators';
import { Display } from '../../../../../models/display/dto/display';
import { HasChildIds } from '../../../../../models/protocols/has-child-ids';
import { Router } from '@angular/router';

@Injectable()
export class SelectLocationDisplaysModalViewModel extends BaseModalViewModel {
  constructor(
    private locationDomainModel: LocationDomainModel,
    private displayDomainModel: DisplayDomainModel,
    private activeModal: NgbActiveModal,
    router: Router,
    ngbModal: NgbModal
  ) {
    super(router, ngbModal);
  }

  public readonly locationId$ = this.locationDomainModel.locationId$;
  public readonly currentLocationDisplays$ = this.displayDomainModel.currentLocationDisplays$;

  private readonly _selectedDisplayIds = new BehaviorSubject<string[]>(null);
  public readonly selectedDisplayIds$ = this._selectedDisplayIds as BehaviorSubject<string[]>;
  connectToSelectedDisplayIds = (selectedRequiredDisplayIds: string[]) => {
    this._selectedDisplayIds.next(selectedRequiredDisplayIds);
  };

  public readonly nSelectedDisplayIds$ = this.selectedDisplayIds$.pipe(map(ids => ids?.length ?? 0));
  public readonly disableClearButton$ = this.nSelectedDisplayIds$.pipe(map(n => n === 0));

  private readonly _searchedDisplays = new BehaviorSubject<Display[]>(null);
  public readonly searchedDisplays$ = this._searchedDisplays as BehaviorSubject<Display[]>;
  connectToSearchedDisplays = (displays: Display[]) => this._searchedDisplays.next(displays);

  public readonly allSearchedDisplays$ = this.searchedDisplays$.pipe(
    map(allDisplays => {
      const landscapeDisplays = allDisplays?.filter(display => !display?.isPortrait());
      const portraitDisplays = allDisplays?.filter(display => display?.isPortrait());
      return {
        landscapeDisplays,
        portraitDisplays
      };
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly allSearchedDisplayIds$ = this.allSearchedDisplays$.pipe(
    map(displays => {
      return [
        ...(displays?.landscapeDisplays?.map(display => display?.getId()) ?? []),
        ...(displays?.portraitDisplays?.map(display => display?.getId()) ?? [])
      ];
    })
  );

  public currentSearchDisplayGroupings$ = this.allSearchedDisplays$.pipe(
    map(({ landscapeDisplays, portraitDisplays }) => {
      return [
        ...(landscapeDisplays?.length ? [{ title: 'Landscape', displays: landscapeDisplays }] : []),
        ...(portraitDisplays?.length ? [{ title: 'Portrait', displays: portraitDisplays }] : [])
      ];
    })
  );

  public readonly displaysVisibleOnScreen$ = this.allSearchedDisplayIds$.pipe(
    map(displayIds => {
      return new (class implements HasChildIds {
        getId = (): string => 'not relevant';
        getChildIds = (): string[] => displayIds ?? [];
      })();
    })
  );

  private addToSelectedDisplayIds = (id: string) => {
    return this.selectedDisplayIds$.once(ids => {
      const updatedIds = [...(ids ?? []), id].unique();
      this.connectToSelectedDisplayIds(updatedIds);
    });
  };

  public addDisplayIds(ids: string[]) {
    ids?.forEach(id => this.addToSelectedDisplayIds(id));
  }

  private removeFromSelectedDisplayIds = (id: string) => {
    this.selectedDisplayIds$.once(ids => this.connectToSelectedDisplayIds(ids?.filter(currId => currId !== id)));
  };

  public removeDisplayIds(ids: string[]) {
    ids?.forEach(id => this.removeFromSelectedDisplayIds(id));
  }

  trackById(index: number, display: Display) {
    return display?.id;
  }

  trackByTitle(index: number, data: { title: string; displays: Display[] }) {
    return data?.title;
  }

  selectDisplays() {}

  clearSelections(): void {
    this.connectToSelectedDisplayIds(null);
  }

  reloadDisplays(): void {
    combineLatest([this.locationId$, this.selectedDisplayIds$, this.currentLocationDisplays$]).once(
      ([locationId, selectedDisplayIds, currentLocationDisplays]) => {
        if (currentLocationDisplays?.every(display => selectedDisplayIds?.includes(display.id))) {
          this.activeModal.close({ locationId: locationId, displayIds: [] });
        } else {
          this.activeModal.close({ locationId: null, displayIds: selectedDisplayIds });
        }
      }
    );
  }
}
